// 保持这个脚本在入口文件的顶部引入。
import React from 'react';
import App from 'next/app';
import dynamic from 'next/dynamic';
import { defineNextApp } from '@/common/next';
// CSS 加载顺序：后加载的优先级更高
import '@/common/font.scss'; // 字体定义
// SDK 全局样式（主题 token、antd、画布等，原 packages/roboneo-sdk/src/index.ts 侧效导入）
/** -- antd start -- */
import 'antd/lib/modal/style/css';
import 'antd/lib/popover/style/css';
import 'antd/lib/tooltip/style/css';
import 'antd/lib/select/style/css';
import 'antd/lib/input/style/css';
import 'antd/lib/slider/style/css';
import 'antd/lib/drawer/style/css';
import 'antd/lib/switch/style/css';
import 'antd/lib/table/style/css';
/** -- antd end -- */
import '$/style/index.scss';
import '$/style/theme_dark.scss';
import '$/style/theme_light.scss';
import '$/style/var.scss';
import '$/style/resetAntd.scss';
// import '@/common/common.scss'; // 基础重置样式
import '../styles/global.css'; // Tailwind 样式（放最后，优先级最高）
import '../styles/global.scss';

import '@meitu/account-login-popup/dist/style.css';
import '@/polyfills/core-js-shim';
import 'intersection-observer';

import { resetGlobalPreloads } from '$/components/NextImage/ImagePreloadContext';
import SvgIconServiceWorkerRegistrar from '@/components/SvgIconServiceWorkerRegistrar';
import WebApmLifecycle from '@/components/WebApmLifecycle';

// 动态加载
const MobileApp = dynamic(() => import('./--mobile/SubApp').then((mod) => mod.default.resolve()));
const RoboneoApp = dynamic(() => import('./--roboneo/SubApp').then((mod) => mod.default.resolve()));
const OverseaRoboneoApp = dynamic(() => import('./--oversea/SubApp').then((mod) => mod.default.resolve()));

function parseCookieString(cookieStr: string) {
  const cookies: Record<string, string> = {};
  if (!cookieStr) return cookies;

  const pairs = cookieStr.split('; ');
  for (const pair of pairs) {
    const [key, ...valParts] = pair.split('=');
    cookies[key] = decodeURIComponent(valParts.join('='));
  }
  return cookies;
}
export default defineNextApp({
  component: (appProps) => {
    const currentProjectId = appProps.Component?.pageConfig?.projectId;

    let pageContent: React.ReactNode;

    if (currentProjectId === '/--mobile') {
      pageContent = <MobileApp appProps={appProps} />;
    } else if (currentProjectId === '/--roboneo') {
      pageContent = <RoboneoApp appProps={appProps} />;
    } else if (currentProjectId === '/--oversea') {
      pageContent = <OverseaRoboneoApp appProps={appProps} />;
    } else {
      pageContent = <appProps.Component />;
    }

    return (
      <>
        <WebApmLifecycle />
        <SvgIconServiceWorkerRegistrar />
        {pageContent}
      </>
    );
  },
  async getInitialProps(appContext) {
    // 每个 SSR 请求开始时重置 preload 收集器
    resetGlobalPreloads();

    const appProps = await App.getInitialProps(appContext);
    const { req, query } = appContext.ctx;
    let countryCode = 'CN';
    const cookieHeader = req?.headers.cookie;
    const cookies = parseCookieString(cookieHeader || '');
    const userAgent = req?.headers['user-agent'];
    const theme = query.theme || cookies.theme || 'dark';
    if (req) {
      // ✅ 3. IP 判断语言
      const ip = (req.headers['x-forwarded-for'] || req.socket.remoteAddress || '') as string;
      try {
        const currentIp = (process.env.NEXT_PUBLIC_ENV === 'development' || process.env.NEXT_PUBLIC_ENV === 'dev') ? '139.9.117.39:80' : ip;
        let prefix = process.env.NEXT_PUBLIC_ENV !== 'release' ? process.env.NEXT_PUBLIC_ENV : '';
        if (prefix === 'development' || prefix === 'dev') {
          prefix = 'pre';
        }
        const urlParams = new URLSearchParams({
          client_id: process.env.NEXT_PUBLIC_REBONEO_CLIENT_ID || '',
          gid: cookies.gid || '',
        });
        const url = `http://${prefix}webapi.roboneo.${(process.env.NEXT_PUBLIC_ENV === 'development' || process.env.NEXT_PUBLIC_ENV === 'dev') ? '' : 'm.'}com/common/ip_resolution.json?${urlParams.toString()}`;
        const geoRes = await fetch(url, {
          method: 'GET', // 也可以省略，fetch 默认是 GET
          headers: {
            'x-forwarded-for': currentIp,
            ...(cookieHeader ? { cookie: cookieHeader } : {}),
          },
        });
        if (!geoRes.ok) {
          const text = await geoRes.text();
          console.error('错误响应体:', text);
          throw new Error(`HTTP 错误，状态码: ${geoRes.status}`);
        } else {
          const geoData = await geoRes.json();
          countryCode = geoData.data.area_code;
          console.error('[IP Resolution]', JSON.stringify({
            countryCode: geoData.data.area_code,
            gid: cookies.gid || '',
            reqid: geoData.reqid,
            currentIp,
            xForwardedFor: req.headers['x-forwarded-for'] || '',
            remoteAddress: req.socket.remoteAddress || '',
            customQuery: query.meituQuery || '',
          }));
        }
      } catch (e) {
        console.warn('Geo IP 语言判断失败', e);
      }
      // 方便测试海外
      if ((process.env.NEXT_PUBLIC_ENV === 'pre' || process.env.NEXT_PUBLIC_ENV === 'dev' || process.env.NEXT_PUBLIC_ENV === 'development') && query.countryCode) {
        countryCode = query.countryCode as any;
      }
    }
    return {
      ...appProps,
      pageProps: {
        ...appProps.pageProps,
        countryCode,
        userAgent,
        theme,
      },
    };
  },
});
